// Sign out when the access token expires (backend auth.py, 60 min default).
//
// The server answers an expired token with 401 session_expired, which
// deviceSession.js already turns into a sign-out — but only once something
// calls the API. A page left open would sit there until the next click failed.
// This watcher reads the token's own `exp` and announces the same
// session-ended event the moment it passes, so SessionEndedGuard signs the
// user out to their login page even with no clicks.

import { SESSION_ENDED_EVENT, SESSION_EXPIRED_CODE } from './deviceSession';

const TOKEN_KEY = 'desky_token';
const CHECK_INTERVAL_MS = 30000;
const EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

const decodeBase64Url = (part) => {
  const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
  return atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '='));
};

/**
 * When the token expires, in ms since epoch — or null if it has no readable `exp`.
 * The signature is not checked; the server does that on every call.
 * @param {string | null | undefined} token
 * @returns {number | null}
 */
export function tokenExpiresAt(token) {
  const payload = typeof token === 'string' ? token.split('.')[1] : null;
  if (!payload) return null;
  try {
    const { exp } = JSON.parse(decodeBase64Url(payload));
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * @param {string | null | undefined} token
 * @param {number} [now]  ms since epoch
 */
export function isTokenExpired(token, now = Date.now()) {
  const expiresAt = tokenExpiresAt(token);
  return expiresAt !== null && now >= expiresAt;
}

const storedToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};

/**
 * Announce session_expired if the signed-in token has run out.
 * @param {number} [now]  ms since epoch
 * @returns {boolean} whether it announced
 */
export function checkSessionExpiry(now = Date.now()) {
  if (!isTokenExpired(storedToken(), now)) return false;
  window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT, {
    detail: { code: SESSION_EXPIRED_CODE, message: EXPIRED_MESSAGE },
  }));
  return true;
}

/**
 * Check now, every 30 s, and whenever the tab comes back into view (timers
 * stall while a laptop sleeps). Reads the stored token on each check, so a
 * new login needs no re-arming.
 * @returns {() => void} stops watching
 */
export function watchSessionExpiry() {
  const check = () => { checkSessionExpiry(); };
  const onVisible = () => { if (document.visibilityState === 'visible') check(); };
  const timer = setInterval(check, CHECK_INTERVAL_MS);
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', onVisible);
  check();
  return () => {
    clearInterval(timer);
    window.removeEventListener('focus', check);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
